import { useEffect, useMemo, useState } from "react";
import { Navigate, NavLink, Routes, Route, useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  LayoutDashboard, Inbox, Globe, Package, Building2, MessageCircle,
  Settings as SettingsIcon, LogOut, Download, Printer, Plus, Edit, Trash2,
  X, CheckCircle2, AlertCircle, KeyRound, Activity, Search,
  ShoppingCart, Tag, Menu, Users,
} from "lucide-react";
import { api, API_BASE, TOKEN_KEY, formatApiError } from "@/api";
import { useAuth } from "@/context/AuthContext";
import PagesCMS from "@/pages/admin/PagesCMS";
import ImageUploadField from "@/pages/admin/ImageUploadField";
import TeamMembersView from "@/pages/admin/TeamMembersView";

const LEAD_STATUSES = ["New", "Contacted", "Follow Up", "Proposal Sent", "Converted", "Closed"];
const STATUS_COLORS = {
  "New": "#3F5A2E", "Contacted": "#5B7544", "Follow Up": "#B8975A",
  "Proposal Sent": "#C4682B", "Converted": "#25D366", "Closed": "#7A7A6F",
};
const INQUIRY_TYPES = [
  "All", "Product Development", "Food Consultancy",
  "Raw Material Supply", "Franchise Inquiry", "General Contact",
];
const COLLECTION_TO_TYPE_KEY = {
  "leads_product_dev": "product-dev",
  "leads_consultancy": "consultancy",
  "leads_contact": "contact",
  "leads_cart_rfq": "cart-rfq",
  "partner_inquiries": "partner",
};

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  // Close mobile drawer on route change
  useEffect(() => { setMobileNavOpen(false); }, [location.pathname]);

  if (user === undefined) {
    return <div className="min-h-screen flex items-center justify-center bg-cream font-mono-label text-[var(--k-muted)]">Loading…</div>;
  }
  if (!user) return <Navigate to="/admin" replace />;

  const handleLogout = async () => {
    try { await api.post("/auth/logout"); } catch (_e) { /* ignore */ }
    logout();
    navigate("/admin", { replace: true });
  };

  const SidebarBody = (
    <>
      <div className="p-6 border-b border-[var(--k-line)] flex items-start justify-between">
        <div>
          <div className="font-mono-label text-[var(--k-gold)] text-[0.55rem]">KOSTRA</div>
          <div className="font-serif-display text-2xl text-[var(--k-text)] mt-1">Admin</div>
          <div className="font-mono-label text-[var(--k-muted)] text-[0.55rem] mt-3">
            Signed in as <span className="text-[var(--k-green)]">{user.username}</span>
          </div>
        </div>
        <button
          type="button"
          onClick={() => setMobileNavOpen(false)}
          className="md:hidden w-9 h-9 rounded-full border border-[var(--k-line)] flex items-center justify-center text-[var(--k-text-2)]"
          data-testid="admin-mobile-nav-close"
          aria-label="Close menu"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
      <nav className="p-3 flex-1 flex flex-col gap-1 overflow-y-auto">
        <NavItem to="/admin/dashboard" icon={LayoutDashboard} label="Dashboard" testId="admin-nav-overview" end />
        <NavItem to="/admin/dashboard/leads" icon={Inbox} label="Lead Management" testId="admin-nav-leads" />
        <NavItem to="/admin/dashboard/cms" icon={Globe} label="Website CMS" testId="admin-nav-cms" />
        <NavItem to="/admin/dashboard/products" icon={Package} label="Product Catalogue" testId="admin-nav-products" />
        <NavItem to="/admin/dashboard/franchise" icon={Building2} label="Franchise Enquiries" testId="admin-nav-franchise" />
        <NavItem to="/admin/dashboard/rfq" icon={MessageCircle} label="WhatsApp RFQs" testId="admin-nav-rfq" />
        <NavItem to="/admin/dashboard/brands" icon={Tag} label="Brands" testId="admin-nav-brands" />
        <NavItem to="/admin/dashboard/team" icon={Users} label="Team Members" testId="admin-nav-team" />
        <NavItem to="/admin/dashboard/settings" icon={SettingsIcon} label="Settings" testId="admin-nav-settings" />
      </nav>
      <button onClick={handleLogout} data-testid="admin-logout-btn" className="m-3 flex items-center gap-3 px-4 py-3 rounded-lg text-[var(--k-text-2)] hover:bg-[var(--k-bg-2)] transition-colors">
        <LogOut className="w-4 h-4" /> <span className="font-mono-label text-[0.65rem]">Sign out</span>
      </button>
    </>
  );

  return (
    <div className="min-h-screen bg-cream md:flex" data-testid="admin-dashboard">
      {/* Mobile top bar */}
      <header className="md:hidden sticky top-0 z-40 flex items-center justify-between gap-3 px-4 py-3 bg-[var(--k-card)] border-b border-[var(--k-line)] admin-no-print">
        <button
          type="button"
          onClick={() => setMobileNavOpen(true)}
          className="w-10 h-10 rounded-lg border border-[var(--k-line)] flex items-center justify-center text-[var(--k-text)]"
          data-testid="admin-mobile-nav-toggle"
          aria-label="Open menu"
        >
          <Menu className="w-5 h-5" />
        </button>
        <div className="flex flex-col items-center leading-tight">
          <span className="font-mono-label text-[var(--k-gold)] text-[0.5rem]">KOSTRA</span>
          <span className="font-serif-display text-base text-[var(--k-text)]">Admin</span>
        </div>
        <button
          onClick={handleLogout}
          className="w-10 h-10 rounded-lg border border-[var(--k-line)] flex items-center justify-center text-[var(--k-text-2)]"
          data-testid="admin-mobile-logout-btn"
          aria-label="Sign out"
        >
          <LogOut className="w-4 h-4" />
        </button>
      </header>

      {/* Desktop sidebar */}
      <aside className="hidden md:flex w-64 bg-[var(--k-card)] border-r border-[var(--k-line)] flex-col shrink-0">
        {SidebarBody}
      </aside>

      {/* Mobile slide-in drawer */}
      <AnimatePresence>
        {mobileNavOpen && (
          <>
            <motion.div
              key="admin-mobile-overlay"
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="md:hidden fixed inset-0 z-[60] bg-black/40 backdrop-blur-sm"
              onClick={() => setMobileNavOpen(false)}
              data-testid="admin-mobile-nav-overlay"
            />
            <motion.aside
              key="admin-mobile-drawer"
              initial={{ x: "-100%" }} animate={{ x: 0 }} exit={{ x: "-100%" }}
              transition={{ type: "tween", duration: 0.25 }}
              className="md:hidden fixed top-0 left-0 bottom-0 z-[70] w-72 max-w-[85%] bg-[var(--k-card)] border-r border-[var(--k-line)] flex flex-col"
              data-testid="admin-mobile-nav-drawer"
            >
              {SidebarBody}
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      <main className="flex-1 min-w-0 overflow-auto">
        <Routes>
          <Route index element={<Overview />} />
          <Route path="leads" element={<LeadsView initialType="All" />} />
          <Route path="franchise" element={<LeadsView initialType="Franchise Inquiry" lockType />} />
          <Route path="rfq" element={<LeadsView initialType="Raw Material Supply" lockType />} />
          <Route path="cms" element={<PagesCMS />} />
          <Route path="products" element={<ProductsView />} />
          <Route path="brands" element={<BrandsView />} />
          <Route path="team" element={<TeamMembersView />} />
          <Route path="settings" element={<SettingsView />} />
        </Routes>
      </main>
    </div>
  );
}

function NavItem({ to, icon: Icon, label, testId, end }) {
  return (
    <NavLink to={to} end={end} data-testid={testId}
      className={({ isActive }) =>
        `flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
          isActive ? "bg-[var(--k-green)] text-[var(--k-bg)]" : "text-[var(--k-text-2)] hover:bg-[var(--k-bg-2)]"
        }`}>
      <Icon className="w-4 h-4" />
      <span className="font-mono-label text-[0.65rem] tracking-wider">{label}</span>
    </NavLink>
  );
}

// ───────── Overview ─────────────────────────────────────────────
function Overview() {
  const [counts, setCounts] = useState(null);
  const [activity, setActivity] = useState([]);
  useEffect(() => {
    api.get("/admin/dashboard").then((r) => setCounts(r.data)).catch(() => {});
    api.get("/admin/recent-activity").then((r) => setActivity(r.data)).catch(() => {});
  }, []);
  const cards = counts ? [
    { label: "Total Leads", value: counts.total_leads, key: "total" },
    { label: "Product Dev", value: counts.leads_product_dev, key: "leads_product_dev" },
    { label: "Food Consultancy", value: counts.leads_consultancy, key: "leads_consultancy" },
    { label: "Raw Material RFQs", value: counts.leads_cart_rfq, key: "leads_cart_rfq" },
    { label: "Franchise Inquiries", value: counts.partner_inquiries, key: "partner_inquiries" },
    { label: "Contact Inquiries", value: counts.leads_contact, key: "leads_contact" },
    { label: "Leads This Month", value: counts.leads_this_month, key: "leads_this_month" },
    { label: "Products / Brands", value: `${counts.products} / ${counts.brands}`, key: "products" },
  ] : [];

  return (
    <div className="p-5 sm:p-8 md:p-12" data-testid="admin-overview">
      <h1 className="font-serif-display text-3xl sm:text-4xl text-[var(--k-text)] mb-2">KOSTRA Admin Dashboard</h1>
      <p className="text-[var(--k-text-2)] mb-10">A snapshot of your operations.</p>
      {!counts ? (
        <div className="font-mono-label text-[var(--k-muted)]">Loading…</div>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 mb-12">
            {cards.map((c) => (
              <motion.div key={c.label} initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} className="organic-card p-5 sm:p-7" data-testid={`overview-card-${c.key}`}>
                <div className="font-mono-label text-[var(--k-muted)] text-[0.55rem] mb-2">{c.label}</div>
                <div className="font-serif-display text-3xl green-gradient-text">{c.value}</div>
              </motion.div>
            ))}
          </div>

          <div className="organic-card p-7" data-testid="recent-activity-panel">
            <div className="flex items-center gap-3 mb-5">
              <Activity className="w-5 h-5 text-[var(--k-green)]" />
              <h2 className="font-serif-display text-2xl text-[var(--k-text)]">Recent Activity</h2>
            </div>
            {activity.length === 0 ? (
              <div className="font-mono-label text-[var(--k-muted)] text-xs py-8 text-center">No activity yet.</div>
            ) : (
              <ul className="divide-y divide-[var(--k-line)]">
                {activity.slice(0, 12).map((a, i) => (
                  <li key={i} className="py-3 flex items-start gap-4" data-testid={`activity-row-${i}`}>
                    <div className="w-2 h-2 rounded-full bg-[var(--k-green)] mt-2 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="text-[var(--k-text)] text-sm">{a.title}</div>
                      {a.subtitle && <div className="text-[var(--k-muted)] text-xs mt-0.5 truncate">{a.subtitle}</div>}
                    </div>
                    <div className="font-mono-label text-[var(--k-muted)] text-[0.55rem] whitespace-nowrap">{formatDate(a.created_at)}</div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function formatDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
  } catch { return iso; }
}

// ───────── Lead Management ──────────────────────────────────────
function LeadsView({ initialType = "All", lockType = false }) {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inquiryType, setInquiryType] = useState(initialType);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/leads");
      setLeads(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const updateStatus = async (lead, newStatus) => {
    const typeKey = COLLECTION_TO_TYPE_KEY[lead.collection];
    await api.patch(`/admin/leads/${typeKey}/${lead.id}/status`, { status: newStatus });
    setLeads((prev) => prev.map((l) => l.id === lead.id ? { ...l, status: newStatus } : l));
  };

  const filtered = useMemo(() => {
    return leads.filter((l) => {
      if (inquiryType !== "All" && l.inquiry_type !== inquiryType) return false;
      if (statusFilter !== "All" && l.status !== statusFilter) return false;
      const q = search.trim().toLowerCase();
      if (q) {
        const hay = `${l.name} ${l.company_name} ${l.mobile} ${l.email} ${l.city} ${l.business_type}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      if (dateFrom && (l.created_at || "") < dateFrom) return false;
      if (dateTo && (l.created_at || "") > dateTo + "T23:59:59Z") return false;
      return true;
    });
  }, [leads, inquiryType, statusFilter, search, dateFrom, dateTo]);

  const exportExcel = async () => {
    // Use existing per-type endpoints for accuracy, or fall back to client CSV
    const typeKeys = inquiryType === "All"
      ? ["product-dev", "consultancy", "contact", "cart-rfq", "partner"]
      : [COLLECTION_TO_TYPE_KEY[Object.entries({
          "leads_product_dev": "Product Development",
          "leads_consultancy": "Food Consultancy",
          "leads_contact": "General Contact",
          "leads_cart_rfq": "Raw Material Supply",
          "partner_inquiries": "Franchise Inquiry",
        }).find(([_, v]) => v === inquiryType)?.[0]]];

    const token = localStorage.getItem(TOKEN_KEY);
    for (const tk of typeKeys.filter(Boolean)) {
      const blob = await fetch(`${API_BASE}/admin/leads/${tk}/export.xlsx`, {
        headers: { Authorization: `Bearer ${token}` },
      }).then((r) => r.blob());
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `kostra_${tk}_${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    }
  };

  const printOrPdf = () => {
    window.print();
  };

  return (
    <div className="p-6 md:p-10" data-testid="admin-leads">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6 admin-no-print">
        <div>
          <h1 className="font-serif-display text-3xl sm:text-4xl text-[var(--k-text)]">Lead Management</h1>
          <p className="text-[var(--k-text-2)] mt-2">{filtered.length} of {leads.length} leads.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button onClick={exportExcel} className="btn-outline-green !text-[0.62rem] !py-2.5 !px-4" data-testid="leads-export-excel"><Download className="w-3.5 h-3.5" /> Excel</button>
          <button onClick={printOrPdf} className="btn-outline-green !text-[0.62rem] !py-2.5 !px-4" data-testid="leads-print-btn"><Printer className="w-3.5 h-3.5" /> Print / PDF</button>
        </div>
      </div>

      {/* Filters */}
      <div className="organic-card p-5 mb-5 admin-no-print" data-testid="leads-filters">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
          <Field label="Search">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[var(--k-muted)]" />
              <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Name, mobile, email, city…" className="kostra-input !pl-9" data-testid="leads-search-input" />
            </div>
          </Field>
          {!lockType && (
            <Field label="Inquiry Type">
              <select value={inquiryType} onChange={(e) => setInquiryType(e.target.value)} className="kostra-input cursor-pointer" data-testid="leads-type-filter" style={{ background: "var(--k-card)" }}>
                {INQUIRY_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </Field>
          )}
          <Field label="Status">
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="kostra-input cursor-pointer" data-testid="leads-status-filter" style={{ background: "var(--k-card)" }}>
              <option value="All">All</option>
              {LEAD_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </Field>
          <Field label="From">
            <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="kostra-input cursor-pointer" data-testid="leads-date-from" />
          </Field>
          <Field label="To">
            <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="kostra-input cursor-pointer" data-testid="leads-date-to" />
          </Field>
        </div>
      </div>

      {loading ? (
        <div className="font-mono-label text-[var(--k-muted)]">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="organic-card p-10 text-center text-[var(--k-muted)] font-mono-label" data-testid="leads-empty">No matching leads.</div>
      ) : (
        <div className="organic-card !p-0 overflow-x-auto admin-print-area" data-testid="leads-table">
          <table className="w-full text-xs">
            <thead className="bg-[var(--k-bg-2)] text-[var(--k-muted)] font-mono-label text-[0.55rem]">
              <tr>
                <th className="text-left px-3 py-3">Lead ID</th>
                <th className="text-left px-3 py-3">Date</th>
                <th className="text-left px-3 py-3">Name</th>
                <th className="text-left px-3 py-3">Company</th>
                <th className="text-left px-3 py-3">Mobile</th>
                <th className="text-left px-3 py-3">Email</th>
                <th className="text-left px-3 py-3">City</th>
                <th className="text-left px-3 py-3">Inquiry</th>
                <th className="text-left px-3 py-3">Status</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((l, idx) => (
                <tr key={l.id} className="border-t border-[var(--k-line)] hover:bg-[var(--k-bg)]/40" data-testid={`leads-row-${idx}`}>
                  <td className="px-3 py-3 font-mono text-[var(--k-muted)] text-[0.6rem]">{(l.id || "").slice(0, 8)}</td>
                  <td className="px-3 py-3 text-[var(--k-text-2)] whitespace-nowrap">{formatDate(l.created_at)}</td>
                  <td className="px-3 py-3 text-[var(--k-text)]">{l.name || "—"}</td>
                  <td className="px-3 py-3 text-[var(--k-text-2)]">{l.company_name || "—"}</td>
                  <td className="px-3 py-3 text-[var(--k-text-2)] whitespace-nowrap">{l.mobile || "—"}</td>
                  <td className="px-3 py-3 text-[var(--k-text-2)] truncate max-w-[180px]">{l.email}</td>
                  <td className="px-3 py-3 text-[var(--k-text-2)]">{l.city || "—"}</td>
                  <td className="px-3 py-3 text-[var(--k-text-2)] whitespace-nowrap">{l.inquiry_type}</td>
                  <td className="px-3 py-3">
                    <select
                      value={l.status || "New"}
                      onChange={(e) => updateStatus(l, e.target.value)}
                      className="text-[0.6rem] font-mono-label tracking-wider px-2 py-1.5 rounded-full border cursor-pointer"
                      style={{ borderColor: STATUS_COLORS[l.status] || "var(--k-line)", color: STATUS_COLORS[l.status] || "var(--k-text)", background: "var(--k-bg)" }}
                      data-testid={`leads-status-select-${idx}`}
                    >
                      {LEAD_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ───────── Products ─────────────────────────────────────────────
const BLANK_PRODUCT = { name: "", category: "", description: "", moq: "", shelf_life: "", storage: "", pack_sizes: [], image: "", rate_per_kg: "", amount: "", is_active: true };
const BLANK_CATEGORY = { slug: "", name: "", description: "", image: "" };

function ProductsView() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [editing, setEditing] = useState(null);
  const [editingCat, setEditingCat] = useState(null);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    const [c, p] = await Promise.all([api.get("/categories"), api.get("/products")]);
    setCategories(c.data); setProducts(p.data); setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const visible = filter === "all" ? products : products.filter((p) => p.category === filter);

  const deleteProduct = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    await api.delete(`/admin/products/${id}`); load();
  };
  const deleteCategory = async (slug) => {
    if (!window.confirm(`Delete category "${slug}"?`)) return;
    try { await api.delete(`/admin/categories/${slug}`); load(); }
    catch (err) { window.alert(formatApiError(err?.response?.data?.detail)); }
  };

  return (
    <div className="p-6 md:p-10" data-testid="admin-products">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <h1 className="font-serif-display text-3xl sm:text-4xl text-[var(--k-text)]">Product Catalogue</h1>
          <p className="text-[var(--k-text-2)] mt-2">{products.length} products across {categories.length} categories.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setEditingCat(BLANK_CATEGORY)} className="btn-outline-green !text-[0.62rem]" data-testid="admin-add-category-btn"><Plus className="w-3.5 h-3.5" /> Category</button>
          <button onClick={() => setEditing(BLANK_PRODUCT)} className="btn-green !text-[0.62rem]" data-testid="admin-add-product-btn"><Plus className="w-3.5 h-3.5" /> Product</button>
        </div>
      </div>

      <div className="flex gap-2 overflow-x-auto pb-3 mb-5">
        <FilterChip active={filter === "all"} onClick={() => setFilter("all")} testId="admin-filter-all">All</FilterChip>
        {categories.map((c) => (
          <div key={c.slug} className="flex items-center gap-1">
            <FilterChip active={filter === c.slug} onClick={() => setFilter(c.slug)} testId={`admin-filter-${c.slug}`}>{c.name}</FilterChip>
            <button onClick={() => setEditingCat(c)} className="text-[var(--k-muted)] hover:text-[var(--k-green)] p-1" title="Edit category" data-testid={`admin-cat-edit-${c.slug}`}><Edit className="w-3 h-3" /></button>
            <button onClick={() => deleteCategory(c.slug)} className="text-[var(--k-muted)] hover:text-red-500 p-1" title="Delete category" data-testid={`admin-cat-delete-${c.slug}`}><Trash2 className="w-3 h-3" /></button>
          </div>
        ))}
      </div>

      {loading ? (
        <div className="font-mono-label text-[var(--k-muted)]">Loading…</div>
      ) : (
        <div className="organic-card !p-0 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-[var(--k-bg-2)] text-[var(--k-muted)] font-mono-label text-[0.55rem]">
              <tr>
                <th className="text-left px-5 py-4">Product</th>
                <th className="text-left px-5 py-4 hidden md:table-cell">Category</th>
                <th className="text-left px-5 py-4 hidden lg:table-cell">MOQ</th>
                <th className="text-left px-5 py-4 hidden lg:table-cell">Rate / Amount</th>
                <th className="text-right px-5 py-4">Actions</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((p) => (
                <tr key={p.id} className="border-t border-[var(--k-line)]" data-testid={`admin-product-row-${p.id}`}>
                  <td className="px-5 py-4">
                    <div className="flex items-center gap-3">
                      <img src={p.image} alt={p.name} className="w-10 h-10 rounded object-cover bg-[var(--k-bg-2)]" />
                      <div>
                        <div className="font-serif-display text-base text-[var(--k-text)]">{p.name}</div>
                        <div className="text-[var(--k-muted)] text-xs line-clamp-1 max-w-[300px]">{p.description}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-5 py-4 hidden md:table-cell text-[var(--k-text-2)]">{p.category}</td>
                  <td className="px-5 py-4 hidden lg:table-cell text-[var(--k-text-2)]">{p.moq}</td>
                  <td className="px-5 py-4 hidden lg:table-cell text-[var(--k-text-2)]">
                    <div className="text-xs leading-tight">
                      <div>{p.rate_per_kg || "—"}<span className="text-[var(--k-muted)] text-[0.6rem]"> /kg</span></div>
                      <div className="text-[var(--k-muted)] text-[0.65rem]">{p.amount || ""}</div>
                    </div>
                  </td>
                  <td className="px-5 py-4 text-right">
                    <button onClick={() => setEditing(p)} className="text-[var(--k-green)] mr-3" data-testid={`admin-edit-${p.id}`}><Edit className="w-4 h-4 inline" /></button>
                    <button onClick={() => deleteProduct(p.id)} className="text-red-500" data-testid={`admin-delete-${p.id}`}><Trash2 className="w-4 h-4 inline" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && <ProductEditor product={editing} categories={categories} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      {editingCat && <CategoryEditor category={editingCat} onClose={() => setEditingCat(null)} onSaved={() => { setEditingCat(null); load(); }} />}
    </div>
  );
}

function FilterChip({ active, onClick, children, testId }) {
  return (
    <button onClick={onClick} data-testid={testId} className={`whitespace-nowrap px-4 py-2 rounded-full font-mono-label text-[0.6rem] border transition-colors ${active ? "border-[var(--k-green)] bg-[var(--k-green)] text-[var(--k-bg)]" : "border-[var(--k-line)] text-[var(--k-text-2)]"}`}>{children}</button>
  );
}

function ProductEditor({ product, categories, onClose, onSaved }) {
  const isNew = !product.id;
  const [form, setForm] = useState({
    ...product,
    pack_sizes: product.pack_sizes?.join(", ") || "",
    category: product.category || categories[0]?.slug || "",
  });
  const [status, setStatus] = useState({ state: "idle", msg: "" });
  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const save = async (e) => {
    e.preventDefault();
    setStatus({ state: "loading", msg: "" });
    const payload = {
      name: form.name, category: form.category, description: form.description,
      moq: form.moq, shelf_life: form.shelf_life, storage: form.storage,
      pack_sizes: form.pack_sizes.split(",").map((s) => s.trim()).filter(Boolean),
      image: form.image,
      rate_per_kg: form.rate_per_kg || "",
      amount: form.amount || "",
      is_active: form.is_active !== false,
    };
    try {
      if (isNew) await api.post("/admin/products", payload);
      else await api.put(`/admin/products/${product.id}`, payload);
      onSaved();
    } catch (err) { setStatus({ state: "error", msg: formatApiError(err?.response?.data?.detail) }); }
  };

  return (
    <ModalShell onClose={onClose} title={`${isNew ? "Add" : "Edit"} Product`} testId="admin-product-editor">
      <form onSubmit={save}>
        <div className="grid md:grid-cols-2 gap-x-7 gap-y-5">
          <Field label="Name *"><input className="kostra-input" required value={form.name} onChange={set("name")} data-testid="editor-input-name" /></Field>
          <Field label="Category *">
            <select className="kostra-input cursor-pointer" required value={form.category} onChange={set("category")} data-testid="editor-select-category" style={{ background: "var(--k-card)" }}>
              {categories.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
            </select>
          </Field>
          <Field label="MOQ"><input className="kostra-input" value={form.moq} onChange={set("moq")} data-testid="editor-input-moq" /></Field>
          <Field label="Shelf Life"><input className="kostra-input" value={form.shelf_life} onChange={set("shelf_life")} data-testid="editor-input-shelf" /></Field>
          <Field label="Rate per Kg"><input className="kostra-input" value={form.rate_per_kg || ""} onChange={set("rate_per_kg")} placeholder="₹450" data-testid="editor-input-rate" /></Field>
          <Field label="Amount"><input className="kostra-input" value={form.amount || ""} onChange={set("amount")} placeholder="₹4,500 / 10Kg" data-testid="editor-input-amount" /></Field>
          <Field label="Storage" full><input className="kostra-input" value={form.storage} onChange={set("storage")} data-testid="editor-input-storage" /></Field>
          <Field label="Pack sizes (comma separated)" full><input className="kostra-input" value={form.pack_sizes} onChange={set("pack_sizes")} data-testid="editor-input-packs" placeholder="500g, 1Kg, 5Kg" /></Field>
          <ImageUploadField
            label="Product Image"
            value={form.image}
            onChange={(url) => setForm((p) => ({ ...p, image: url }))}
            testId="editor-image"
          />
          <Field label="Description" full><textarea className="kostra-input resize-none" rows={3} value={form.description} onChange={set("description")} data-testid="editor-input-description" /></Field>
        </div>
        {status.state === "error" && <ErrorBanner msg={status.msg} testId="editor-error" />}
        <button type="submit" disabled={status.state === "loading"} className="btn-green mt-7 w-full" data-testid="editor-save-btn">
          {status.state === "loading" ? "Saving…" : isNew ? "Create Product" : "Save Changes"}
        </button>
      </form>
    </ModalShell>
  );
}

function CategoryEditor({ category, onClose, onSaved }) {
  const isNew = !category.slug || !category._existing;
  const editing = !!category.slug;
  const [form, setForm] = useState({ ...category, _existing: editing });
  const [status, setStatus] = useState({ state: "idle", msg: "" });
  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const save = async (e) => {
    e.preventDefault();
    setStatus({ state: "loading", msg: "" });
    const payload = { slug: form.slug, name: form.name, description: form.description || "", image: form.image || "" };
    try {
      if (editing) await api.put(`/admin/categories/${category.slug}`, payload);
      else await api.post("/admin/categories", payload);
      onSaved();
    } catch (err) { setStatus({ state: "error", msg: formatApiError(err?.response?.data?.detail) }); }
  };

  return (
    <ModalShell onClose={onClose} title={`${editing ? "Edit" : "Add"} Category`} testId="admin-category-editor">
      <form onSubmit={save}>
        <div className="grid md:grid-cols-2 gap-x-7 gap-y-5">
          <Field label="Slug *"><input className="kostra-input" required value={form.slug} onChange={set("slug")} disabled={editing} placeholder="bakery-solutions" data-testid="cat-input-slug" /></Field>
          <Field label="Name *"><input className="kostra-input" required value={form.name} onChange={set("name")} data-testid="cat-input-name" /></Field>
          <ImageUploadField
            label="Category Image"
            value={form.image || ""}
            onChange={(url) => setForm((p) => ({ ...p, image: url }))}
            testId="cat-image"
          />
          <Field label="Description" full><textarea className="kostra-input resize-none" rows={3} value={form.description || ""} onChange={set("description")} data-testid="cat-input-description" /></Field>
        </div>
        {status.state === "error" && <ErrorBanner msg={status.msg} testId="cat-error" />}
        <button type="submit" disabled={status.state === "loading"} className="btn-green mt-7 w-full" data-testid="cat-save-btn">
          {status.state === "loading" ? "Saving…" : editing ? "Save Changes" : "Create Category"}
        </button>
      </form>
    </ModalShell>
  );
}

// ───────── Brands ───────────────────────────────────────────────
const BLANK_BRAND = { name: "", description: "", logo: "", image: "", website: "", instagram: "", facebook: "", is_active: true };

function BrandsView() {
  const [brands, setBrands] = useState([]);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    const { data } = await api.get("/admin/brands");
    setBrands(data); setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!window.confirm("Delete this brand?")) return;
    await api.delete(`/admin/brands/${id}`); load();
  };

  return (
    <div className="p-6 md:p-10" data-testid="admin-brands">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <h1 className="font-serif-display text-3xl sm:text-4xl text-[var(--k-text)]">Brands</h1>
          <p className="text-[var(--k-text-2)] mt-2">{brands.length} brands under KOSTRA.</p>
        </div>
        <button onClick={() => setEditing(BLANK_BRAND)} className="btn-green" data-testid="admin-add-brand-btn">
          <Plus className="w-4 h-4" /> Add Brand
        </button>
      </div>

      {loading ? (
        <div className="font-mono-label text-[var(--k-muted)]">Loading…</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {brands.map((b) => (
            <div key={b.id} className="organic-card p-6" data-testid={`admin-brand-card-${b.id}`}>
              <div className="h-32 mb-4 rounded overflow-hidden bg-[var(--k-bg-2)]">
                {b.image && <img src={b.image} alt={b.name} className="w-full h-full object-cover" />}
              </div>
              <h3 className="font-serif-display text-xl text-[var(--k-text)] mb-1">{b.name}</h3>
              <p className="text-[var(--k-text-2)] text-xs leading-relaxed line-clamp-3 mb-4">{b.description}</p>
              <div className="flex gap-3 text-[0.6rem] font-mono-label">
                <button onClick={() => setEditing(b)} className="text-[var(--k-green)]" data-testid={`admin-edit-brand-${b.id}`}><Edit className="w-3 h-3 inline mr-1" /> Edit</button>
                <button onClick={() => remove(b.id)} className="text-red-500" data-testid={`admin-delete-brand-${b.id}`}><Trash2 className="w-3 h-3 inline mr-1" /> Delete</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && <BrandEditor brand={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function BrandEditor({ brand, onClose, onSaved }) {
  const isNew = !brand.id;
  const [form, setForm] = useState({ ...brand });
  const [status, setStatus] = useState({ state: "idle", msg: "" });
  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const save = async (e) => {
    e.preventDefault();
    setStatus({ state: "loading", msg: "" });
    const payload = {
      name: form.name, description: form.description || "", logo: form.logo || "",
      image: form.image || "", website: form.website || "",
      instagram: form.instagram || "", facebook: form.facebook || "",
      is_active: form.is_active !== false,
    };
    try {
      if (isNew) await api.post("/admin/brands", payload);
      else await api.put(`/admin/brands/${brand.id}`, payload);
      onSaved();
    } catch (err) { setStatus({ state: "error", msg: formatApiError(err?.response?.data?.detail) }); }
  };

  return (
    <ModalShell onClose={onClose} title={`${isNew ? "Add" : "Edit"} Brand`} testId="admin-brand-editor">
      <form onSubmit={save}>
        <div className="grid md:grid-cols-2 gap-x-7 gap-y-5">
          <Field label="Name *"><input className="kostra-input" required value={form.name} onChange={set("name")} data-testid="brand-input-name" /></Field>
          <Field label="Website"><input className="kostra-input" value={form.website || ""} onChange={set("website")} data-testid="brand-input-website" /></Field>
          <ImageUploadField
            label="Brand Image"
            value={form.image || ""}
            onChange={(url) => setForm((p) => ({ ...p, image: url }))}
            testId="brand-image"
          />
          <ImageUploadField
            label="Brand Logo"
            value={form.logo || ""}
            onChange={(url) => setForm((p) => ({ ...p, logo: url }))}
            testId="brand-logo"
          />
          <Field label="Instagram"><input className="kostra-input" value={form.instagram || ""} onChange={set("instagram")} data-testid="brand-input-instagram" /></Field>
          <Field label="Facebook"><input className="kostra-input" value={form.facebook || ""} onChange={set("facebook")} data-testid="brand-input-facebook" /></Field>
          <Field label="Description" full><textarea className="kostra-input resize-none" rows={3} value={form.description || ""} onChange={set("description")} data-testid="brand-input-description" /></Field>
        </div>
        {status.state === "error" && <ErrorBanner msg={status.msg} testId="brand-error" />}
        <button type="submit" disabled={status.state === "loading"} className="btn-green mt-7 w-full" data-testid="brand-save-btn">
          {status.state === "loading" ? "Saving…" : isNew ? "Create Brand" : "Save Changes"}
        </button>
      </form>
    </ModalShell>
  );
}

// ───────── Settings ─────────────────────────────────────────────
function SettingsView() {
  const [form, setForm] = useState(null);
  const [status, setStatus] = useState({ state: "idle", msg: "" });
  const [pwOpen, setPwOpen] = useState(false);

  useEffect(() => { api.get("/admin/settings").then((r) => setForm(r.data)).catch(() => {}); }, []);
  if (!form) return <div className="p-10 font-mono-label text-[var(--k-muted)]">Loading…</div>;

  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));
  const setList = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) }));

  const save = async (e) => {
    e.preventDefault();
    setStatus({ state: "loading", msg: "" });
    try {
      await api.put("/admin/settings", form);
      setStatus({ state: "success", msg: "Settings saved." });
    } catch (err) { setStatus({ state: "error", msg: formatApiError(err?.response?.data?.detail) }); }
  };

  return (
    <div className="p-6 md:p-10 max-w-4xl" data-testid="admin-settings">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
        <div>
          <h1 className="font-serif-display text-3xl sm:text-4xl text-[var(--k-text)]">Settings</h1>
          <p className="text-[var(--k-text-2)] mt-2">Company details, contact lists, social links.</p>
        </div>
        <button onClick={() => setPwOpen(true)} className="btn-outline-green !text-[0.62rem]" data-testid="admin-change-pw-btn">
          <KeyRound className="w-3.5 h-3.5" /> Change Password
        </button>
      </div>

      <form onSubmit={save} className="organic-card p-8 space-y-6" data-testid="admin-settings-form">
        <div className="font-mono-label text-[var(--k-gold)] mb-1">Company Info</div>
        <div className="grid md:grid-cols-2 gap-x-7 gap-y-5">
          <Field label="Company Name"><input className="kostra-input" value={form.company_name || ""} onChange={set("company_name")} data-testid="settings-input-company" /></Field>
          <Field label="GST Number"><input className="kostra-input" value={form.gst_number || ""} onChange={set("gst_number")} data-testid="settings-input-gst" /></Field>
          <Field label="PAN Number"><input className="kostra-input" value={form.pan_number || ""} onChange={set("pan_number")} data-testid="settings-input-pan" /></Field>
          <Field label="CIN Number"><input className="kostra-input" value={form.cin_number || ""} onChange={set("cin_number")} data-testid="settings-input-cin" /></Field>
        </div>

        <hr className="border-[var(--k-line)]" />

        <div className="font-mono-label text-[var(--k-gold)] mb-1">Contact (comma separated)</div>
        <div className="grid md:grid-cols-2 gap-x-7 gap-y-5">
          <Field label="Mobile Numbers" full><input className="kostra-input" defaultValue={(form.mobile_numbers || []).join(", ")} onBlur={setList("mobile_numbers")} data-testid="settings-input-mobiles" /></Field>
          <Field label="WhatsApp Numbers" full><input className="kostra-input" defaultValue={(form.whatsapp_numbers || []).join(", ")} onBlur={setList("whatsapp_numbers")} data-testid="settings-input-whatsapps" /></Field>
          <Field label="Emails" full><input className="kostra-input" defaultValue={(form.emails || []).join(", ")} onBlur={setList("emails")} data-testid="settings-input-emails" /></Field>
        </div>

        <hr className="border-[var(--k-line)]" />

        <div className="font-mono-label text-[var(--k-gold)] mb-1">Social Media</div>
        <div className="grid md:grid-cols-2 gap-x-7 gap-y-5">
          <Field label="Instagram URL"><input className="kostra-input" value={form.instagram || ""} onChange={set("instagram")} data-testid="settings-input-instagram" /></Field>
          <Field label="Facebook URL"><input className="kostra-input" value={form.facebook || ""} onChange={set("facebook")} data-testid="settings-input-facebook" /></Field>
          <Field label="LinkedIn URL"><input className="kostra-input" value={form.linkedin || ""} onChange={set("linkedin")} data-testid="settings-input-linkedin" /></Field>
          <Field label="YouTube URL"><input className="kostra-input" value={form.youtube || ""} onChange={set("youtube")} data-testid="settings-input-youtube" /></Field>
        </div>

        {status.state === "success" && <SuccessBanner msg={status.msg} testId="settings-success" />}
        {status.state === "error" && <ErrorBanner msg={status.msg} testId="settings-error" />}

        <button type="submit" disabled={status.state === "loading"} className="btn-green" data-testid="settings-save-btn">
          Save Settings
        </button>
      </form>

      {pwOpen && <PasswordModal onClose={() => setPwOpen(false)} />}
    </div>
  );
}

function PasswordModal({ onClose }) {
  const [form, setForm] = useState({ current_password: "", new_password: "", confirm: "" });
  const [status, setStatus] = useState({ state: "idle", msg: "" });
  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const save = async (e) => {
    e.preventDefault();
    if (form.new_password !== form.confirm) {
      setStatus({ state: "error", msg: "New password and confirmation don't match." });
      return;
    }
    if (form.new_password.length < 6) {
      setStatus({ state: "error", msg: "Password must be at least 6 characters." });
      return;
    }
    setStatus({ state: "loading", msg: "" });
    try {
      await api.post("/admin/change-password", {
        current_password: form.current_password, new_password: form.new_password,
      });
      setStatus({ state: "success", msg: "Password changed successfully." });
      setForm({ current_password: "", new_password: "", confirm: "" });
    } catch (err) { setStatus({ state: "error", msg: formatApiError(err?.response?.data?.detail) }); }
  };

  return (
    <ModalShell onClose={onClose} title="Change Password" testId="admin-password-modal">
      <form onSubmit={save} className="space-y-5">
        <Field label="Current Password" full><input type="password" className="kostra-input" required value={form.current_password} onChange={set("current_password")} data-testid="pw-input-current" /></Field>
        <Field label="New Password" full><input type="password" className="kostra-input" required value={form.new_password} onChange={set("new_password")} data-testid="pw-input-new" /></Field>
        <Field label="Confirm New Password" full><input type="password" className="kostra-input" required value={form.confirm} onChange={set("confirm")} data-testid="pw-input-confirm" /></Field>
        {status.state === "success" && <SuccessBanner msg={status.msg} testId="pw-success" />}
        {status.state === "error" && <ErrorBanner msg={status.msg} testId="pw-error" />}
        <button type="submit" disabled={status.state === "loading"} className="btn-green w-full" data-testid="pw-save-btn">
          {status.state === "loading" ? "Updating…" : "Update Password"}
        </button>
      </form>
    </ModalShell>
  );
}

// ───────── Shared bits ──────────────────────────────────────────
function Field({ label, full, children }) {
  return (
    <label className={`flex flex-col gap-2 ${full ? "md:col-span-2" : ""}`}>
      <span className="font-mono-label text-[var(--k-muted)]">{label}</span>
      {children}
    </label>
  );
}

function ModalShell({ children, onClose, title, testId }) {
  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[200] flex items-end md:items-center justify-center" onClick={onClose}>
      <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} onClick={(e) => e.stopPropagation()}
        className="bg-[var(--k-bg)] rounded-t-2xl md:rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-8" data-testid={testId}>
        <div className="flex items-center justify-between mb-7">
          <h2 className="font-serif-display text-3xl text-[var(--k-text)]">{title}</h2>
          <button type="button" onClick={onClose} className="w-9 h-9 rounded-full border border-[var(--k-line)] flex items-center justify-center" data-testid={`${testId}-close`}><X className="w-4 h-4" /></button>
        </div>
        {children}
      </motion.div>
    </div>
  );
}

function ErrorBanner({ msg, testId }) {
  return (
    <div className="flex items-start gap-3 border border-red-300 bg-red-50 p-3 rounded mt-5" data-testid={testId}>
      <AlertCircle className="w-4 h-4 text-red-600 mt-0.5" />
      <p className="text-[var(--k-text)] text-xs">{msg}</p>
    </div>
  );
}

function SuccessBanner({ msg, testId }) {
  return (
    <div className="flex items-start gap-3 border border-[var(--k-olive)] bg-[rgba(143,166,120,0.12)] p-3 rounded mt-5" data-testid={testId}>
      <CheckCircle2 className="w-4 h-4 text-[var(--k-green)] mt-0.5" />
      <p className="text-[var(--k-text)] text-xs">{msg}</p>
    </div>
  );
}
